"use client";

import { useEffect, useRef } from "react";
import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
  type InfiniteData,
} from "@tanstack/react-query";

import {
  createComment,
  createFeedPost,
  createPollPost,
  deleteOwnPost,
  getCommunityFeedPosts,
  getFeedPostById,
  getFeedPosts,
  getHomeUnseenPostCounts,
  getPostComments,
  getSavedFeedPosts,
  markFeedPostsSeen,
  removeUploadedPostAttachments,
  setPostReaction,
  setPostSaved,
  submitPollVote,
  updateOwnPostContent,
  uploadPostAttachments,
} from "@/lib/feed/api";
import {
  emptyReactionCounts,
  type CreatePostAttachmentInput,
  type CreatePollInput,
  type FeedCursor,
  type FeedFilter,
  type FeedPage,
  type FeedPost,
  type PostType,
  type ReactionType,
} from "@/lib/feed/types";
import type { LinkPreview } from "@/lib/feed/link-preview";
import { queryKeys } from "@/lib/query-keys";
import { getErrorMessage } from "@/lib/utils";

type FeedInfiniteData = InfiniteData<FeedPage>;

function mapFeedPosts(
  data: FeedInfiniteData | undefined,
  transform: (post: FeedPost) => FeedPost | null,
) {
  if (!data) {
    return data;
  }

  return {
    ...data,
    pages: data.pages.map((page) => ({
      ...page,
      posts: page.posts.flatMap((post) => {
        const next = transform(post);
        return next ? [next] : [];
      }),
    })),
  };
}

function flattenFeedPages(data: FeedInfiniteData | undefined) {
  return data?.pages.flatMap((page) => page.posts) ?? [];
}

export function useFeedPost(postId: string | null, enabled: boolean) {
  return useQuery({
    queryKey: queryKeys.feedPost(postId ?? "none"),
    queryFn: () => getFeedPostById(postId as string),
    enabled: enabled && Boolean(postId),
  });
}

export function useFeedPosts(
  enabled: boolean,
  filter: FeedFilter = "all",
) {
  const query = useInfiniteQuery({
    queryKey: queryKeys.filteredFeed(filter),
    queryFn: ({ pageParam }) => getFeedPosts(pageParam, filter),
    enabled,
    initialPageParam: undefined as FeedCursor | undefined,
    getNextPageParam: (lastPage) => lastPage.nextCursor ?? undefined,
  });
  return { ...query, posts: flattenFeedPages(query.data) };
}

export function useSavedFeedPosts(enabled: boolean) {
  const query = useInfiniteQuery({
    queryKey: queryKeys.savedPosts,
    queryFn: ({ pageParam }) => getSavedFeedPosts(pageParam),
    enabled,
    initialPageParam: undefined as FeedCursor | undefined,
    getNextPageParam: (lastPage) => lastPage.nextCursor ?? undefined,
  });
  return { ...query, posts: flattenFeedPages(query.data) };
}

export function useCommunityFeedPosts(
  communityId: string,
  enabled: boolean,
) {
  const query = useInfiniteQuery({
    queryKey: queryKeys.communityFeed(communityId),
    queryFn: ({ pageParam }) => getCommunityFeedPosts(communityId, pageParam),
    enabled: enabled && Boolean(communityId),
    initialPageParam: undefined as FeedCursor | undefined,
    getNextPageParam: (lastPage) => lastPage.nextCursor ?? undefined,
  });
  return { ...query, posts: flattenFeedPages(query.data) };
}

export function usePostComments(postId: string, enabled: boolean) {
  return useQuery({
    queryKey: queryKeys.comments(postId),
    queryFn: () => getPostComments(postId),
    enabled,
  });
}

export function useHomeUnseenPostCounts(enabled: boolean) {
  return useQuery({
    queryKey: queryKeys.homeUnseenPosts,
    queryFn: getHomeUnseenPostCounts,
    enabled,
  });
}

export function useMarkFeedPostsSeen() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: markFeedPostsSeen,
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: queryKeys.homeUnseenPosts,
      });
    },
  });
}

export function useTrackVisibleFeedPosts(
  posts: FeedPost[],
  enabled: boolean,
) {
  const markedIds = useRef(new Set<string>());
  const { mutate } = useMarkFeedPostsSeen();
  const postIdKey = posts.map((post) => post.id).join(",");

  useEffect(() => {
    if (!enabled || !postIdKey) {
      return;
    }

    const newIds = postIdKey
      .split(",")
      .filter((postId) => !markedIds.current.has(postId));
    if (newIds.length === 0) {
      return;
    }

    newIds.forEach((postId) => markedIds.current.add(postId));
    mutate(newIds);
  }, [enabled, mutate, postIdKey]);
}

export function useCreatePost() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: {
      content: string;
      audience: "global" | "community";
      communityId: string | null;
      postType: PostType;
      poll?: CreatePollInput;
      files: File[];
      userId: string;
      linkPreview?: LinkPreview | null;
    }) => {
      if (input.postType === "poll") {
        if (!input.poll) {
          throw new Error("A szavazás adatai hiányoznak.");
        }
        return createPollPost({
          content: input.content,
          audience: input.audience,
          communityId: input.communityId,
          poll: input.poll,
        });
      }

      let attachments: CreatePostAttachmentInput[] = [];
      if (input.files.length > 0) {
        attachments = await uploadPostAttachments(input.files, input.userId);
      }

      try {
        return await createFeedPost({
          content: input.content,
          audience: input.audience,
          communityId: input.communityId,
          postType: input.postType,
          attachments,
          linkPreview: input.linkPreview ?? null,
        });
      } catch (error) {
        const cleanupError = await removeUploadedPostAttachments(attachments);
        if (cleanupError) {
          throw new Error(
            `${getErrorMessage(error)} A feltöltött fájlok takarítása is sikertelen: ${getErrorMessage(cleanupError)}`,
          );
        }
        throw error;
      }
    },
    onSuccess: async (_postId, input) => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: queryKeys.feed }),
        input.communityId
          ? queryClient.invalidateQueries({
              queryKey: queryKeys.communityFeed(input.communityId),
            })
          : Promise.resolve(),
      ]);
    },
  });
}

export function useSubmitPollVote(postId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: { optionIds: string[]; otherText: string | null }) =>
      submitPollVote({
        postId,
        optionIds: input.optionIds,
        otherText: input.otherText,
      }),
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: queryKeys.feed }),
        queryClient.invalidateQueries({ queryKey: ["community-feed"] }),
        queryClient.invalidateQueries({ queryKey: queryKeys.savedPosts }),
      ]);
    },
  });
}

export function useUpdateOwnPost(postId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: {
      content: string;
      linkPreview?: LinkPreview | null;
      removeLinkPreview?: boolean;
    }) =>
      updateOwnPostContent({
        postId,
        content: input.content,
        linkPreview: input.linkPreview,
        removeLinkPreview: input.removeLinkPreview,
      }),
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: queryKeys.feed }),
        queryClient.invalidateQueries({ queryKey: ["community-feed"] }),
        queryClient.invalidateQueries({ queryKey: queryKeys.savedPosts }),
      ]);
    },
  });
}

export function useDeleteOwnPost(postId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => deleteOwnPost(postId),
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: queryKeys.feed }),
        queryClient.invalidateQueries({ queryKey: ["community-feed"] }),
        queryClient.invalidateQueries({ queryKey: queryKeys.savedPosts }),
      ]);
    },
  });
}

export function useToggleReaction(postId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: {
      userId: string;
      current: ReactionType | null;
      next: ReactionType;
    }) =>
      setPostReaction({
        postId,
        userId: input.userId,
        current: input.current,
        next: input.next,
      }),
    onMutate: async (input) => {
      const feedPredicate = (query: { queryKey: readonly unknown[] }) =>
        query.queryKey[0] === "feed" ||
        query.queryKey[0] === "community-feed";
      await queryClient.cancelQueries({ predicate: feedPredicate });
      const previous = queryClient.getQueriesData<FeedInfiniteData>({
        predicate: feedPredicate,
      });

      queryClient.setQueriesData<FeedInfiniteData>(
        { predicate: feedPredicate },
        (data) =>
          mapFeedPosts(data, (post) => {
          if (post.id !== postId) {
            return post;
          }

          const reactionCounts = { ...emptyReactionCounts(), ...post.reactionCounts };

          if (post.myReaction) {
            reactionCounts[post.myReaction] = Math.max(
              0,
              reactionCounts[post.myReaction] - 1,
            );
          }

          const nextReaction = post.myReaction === input.next ? null : input.next;

          if (nextReaction) {
            reactionCounts[nextReaction] += 1;
          }

          return {
            ...post,
            reactionCounts,
            myReaction: nextReaction,
          };
          }),
      );

      return { previous };
    },
    onError: (_error, _input, context) => {
      for (const [queryKey, data] of context?.previous ?? []) {
        queryClient.setQueryData(queryKey, data);
      }
    },
  });
}

export function useToggleSavedPost(postId: string) {
  const queryClient = useQueryClient();
  const feedPredicate = (query: { queryKey: readonly unknown[] }) =>
    query.queryKey[0] === "feed" ||
    query.queryKey[0] === "community-feed" ||
    query.queryKey[0] === "saved-posts";

  return useMutation({
    mutationFn: (input: { userId: string; saved: boolean }) =>
      setPostSaved({
        postId,
        userId: input.userId,
        saved: input.saved,
      }),
    onMutate: async (input) => {
      await queryClient.cancelQueries({ predicate: feedPredicate });
      const previous = queryClient.getQueriesData<FeedInfiniteData>({
        predicate: feedPredicate,
      });

      queryClient.setQueriesData<FeedInfiniteData>(
        { predicate: feedPredicate },
        (data) =>
          mapFeedPosts(data, (post) =>
            post.id === postId
              ? { ...post, isSaved: input.saved }
              : post,
          ),
      );

      if (!input.saved) {
        queryClient.setQueryData<FeedInfiniteData>(
          queryKeys.savedPosts,
          (data) => mapFeedPosts(data, (post) => (post.id === postId ? null : post)),
        );
      }

      return { previous };
    },
    onError: (_error, _input, context) => {
      for (const [queryKey, data] of context?.previous ?? []) {
        queryClient.setQueryData(queryKey, data);
      }
    },
    onSuccess: async (_result, input) => {
      if (input.saved) {
        await queryClient.invalidateQueries({ queryKey: queryKeys.savedPosts });
      }
    },
  });
}

export function useCreateComment(postId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: {
      userId: string;
      content: string;
      parentCommentId?: string | null;
    }) =>
      createComment({
        postId,
        userId: input.userId,
        content: input.content,
        parentCommentId: input.parentCommentId,
      }),
    onSuccess: async () => {
      queryClient.setQueriesData<FeedInfiniteData>(
        {
          predicate: (query) =>
            query.queryKey[0] === "feed" ||
            query.queryKey[0] === "community-feed" ||
            query.queryKey[0] === "saved-posts",
        },
        (data) =>
          mapFeedPosts(data, (post) =>
            post.id === postId
              ? { ...post, commentCount: post.commentCount + 1 }
              : post,
          ),
      );
      await queryClient.invalidateQueries({
        queryKey: queryKeys.comments(postId),
      });
    },
  });
}
